import {produce} from "immer";
import {Store} from "reducers/rootReducer";
import {from, Observable} from "rxjs";
import {switchMap, catchError, ignoreElements, filter, map} from "rxjs/operators";
import {combineEpics, ofType, ActionsObservable} from "redux-observable";
import {AxiosError} from "axios";

export type ProjectDetails = {
  id: number;
  name: string;
  organisationalStructuresID: number;
  auroraDatabaseName: string;
  clientUserName: string;
  clientLogoLink: string;
  clientID: number;
  bUserHasAccessToMultipleLocations: boolean;
  userLocationID: number;
  bUsesReviewPush: boolean;
  bUsesBrightLocal: boolean;
  bCanAccessAccountManagement: boolean;
  bCanAccessUserManagement: boolean;
  bCanAccessDashboard: boolean;
  bCanAccessCommentAnalysis: boolean;
  bCanAccessReviewFeed: boolean;
  bCanAccessLeagueTables: boolean;
  bCanAccessLoginReport: boolean;
  bCanAccessResources: boolean;
  bCanAccessGuides: boolean;
  bCanAccessQuicksight: boolean;
  bCanAccessReviewConsolidation: boolean;
};

export interface UserAccessFields {
  bCanAccessAccountManagement: boolean;
  bCanAccessUserManagement: boolean;
  bCanAccessDashboard: boolean;
  bCanAccessCommentAnalysis: boolean;
  bCanAccessReviewFeed: boolean;
  bCanAccessLeagueTables: boolean;
  bCanAccessLoginReport: boolean;
  bCanAccessResources: boolean;
  bCanAccessGuides: boolean;
  bCanAccessQuicksight: boolean;
  bCanAccessReviewConsolidation: boolean;
}

/* STATE */
type State = {
  projectIdValid: "idle" | "valid" | "invalid";
  projectId: number | null;
  projectName: string;
  organisationalStructuresID: number | null;
  auroraDatabaseName: string;
  clientUserName: string;
  clientID: number | null;
  clientLogoLink: string;
  multipleLocations: boolean | null;
  userLocationID: number | null;
  bUsesReviewPush: boolean | null;
  bUsesBrightLocal: boolean | null;
  bCanAccessAccountManagement: boolean | null;
  bCanAccessUserManagement: boolean | null;
  bCanAccessDashboard: boolean | null;
  bCanAccessCommentAnalysis: boolean | null;
  bCanAccessReviewFeed: boolean | null;
  bCanAccessReviewConsolidation: boolean | null;
  bCanAccessLeagueTables: boolean | null;
  bCanAccessLoginReport: boolean | null;
  bCanAccessResources: boolean | null;
  bCanAccessGuides: boolean | null;
  bCanAccessQuicksight: boolean | null;
};

const initialState: State = {
  projectIdValid: "idle",
  projectId: null,
  projectName: "",
  organisationalStructuresID: null,
  auroraDatabaseName: "",
  clientUserName: "",
  clientLogoLink: "",
  clientID: null,
  multipleLocations: null,
  userLocationID: null,
  bUsesReviewPush: null,
  bUsesBrightLocal: null,
  bCanAccessAccountManagement: null,
  bCanAccessUserManagement: null,
  bCanAccessDashboard: null,
  bCanAccessCommentAnalysis: null,
  bCanAccessReviewFeed: null,
  bCanAccessLeagueTables: null,
  bCanAccessLoginReport: null,
  bCanAccessResources: null,
  bCanAccessGuides: null,
  bCanAccessQuicksight: null,
  bCanAccessReviewConsolidation: null
};

/* ACTIONS */
export const SET_PROJECT = "SET_PROJECT";
const PROJECT_ID_VALID = "PROJECT_ID_VALID";

type Action =
  | { type: typeof SET_PROJECT; payload: ProjectDetails }
  | {
      type: typeof PROJECT_ID_VALID;
      payload: "idle" | "valid" | "invalid";
      details?: ProjectDetails;
    };

/* REDUCER */
export default function reducer(state = initialState, action: Action): State {
  return produce(state, (draft) => {
    switch (action.type) {
      case SET_PROJECT:
        draft.projectId = action.payload.id;
        draft.projectName = action.payload.name;
        draft.organisationalStructuresID =
          action.payload.organisationalStructuresID;
        draft.auroraDatabaseName = action.payload.auroraDatabaseName;
        draft.clientUserName = action.payload.clientUserName;
        draft.clientID = action.payload.clientID;
        draft.multipleLocations =
          action.payload.bUserHasAccessToMultipleLocations;
        draft.userLocationID = action.payload.userLocationID;
        draft.bUsesReviewPush = action.payload.bUsesReviewPush;
        draft.bUsesBrightLocal = action.payload.bUsesBrightLocal;
        draft.bCanAccessAccountManagement =
          action.payload.bCanAccessAccountManagement;
        draft.bCanAccessUserManagement =
          action.payload.bCanAccessUserManagement;
        draft.bCanAccessDashboard = action.payload.bCanAccessDashboard;
        draft.bCanAccessCommentAnalysis =
          action.payload.bCanAccessCommentAnalysis;
        draft.bCanAccessReviewFeed = action.payload.bCanAccessReviewFeed;
        draft.bCanAccessLeagueTables = action.payload.bCanAccessLeagueTables;
        draft.bCanAccessLoginReport = action.payload.bCanAccessLoginReport;
        draft.bCanAccessResources = action.payload.bCanAccessResources;
        draft.bCanAccessGuides = action.payload.bCanAccessGuides;
        draft.bCanAccessQuicksight = action.payload.bCanAccessQuicksight;
        draft.bCanAccessReviewConsolidation = action.payload.bCanAccessReviewConsolidation
        break;
      case PROJECT_ID_VALID:
        draft.projectIdValid = action.payload;
        if (action.details) {
          draft.projectId = action.details.id;
          draft.projectName = action.details.name;
          draft.organisationalStructuresID =
            action.details.organisationalStructuresID;
          draft.auroraDatabaseName = action.details.auroraDatabaseName;
          draft.clientUserName = action.details.clientUserName;
          draft.clientID = action.details.clientID;
          draft.multipleLocations =
            action.details.bUserHasAccessToMultipleLocations;
          draft.userLocationID = action.details.userLocationID;
          draft.bUsesReviewPush = action.details.bUsesReviewPush;
          draft.bUsesBrightLocal = action.details.bUsesBrightLocal;
          draft.bCanAccessAccountManagement =
            action.details.bCanAccessAccountManagement;
          draft.bCanAccessUserManagement =
            action.details.bCanAccessUserManagement;
          draft.bCanAccessDashboard = action.details.bCanAccessDashboard;
          draft.bCanAccessCommentAnalysis =
            action.details.bCanAccessCommentAnalysis;
          draft.bCanAccessReviewFeed = action.details.bCanAccessReviewFeed;
          draft.bCanAccessLeagueTables = action.details.bCanAccessLeagueTables;
          draft.bCanAccessLoginReport = action.details.bCanAccessLoginReport;
          draft.bCanAccessResources = action.details.bCanAccessResources;
          draft.bCanAccessGuides = action.details.bCanAccessGuides;
          draft.bCanAccessQuicksight = action.details.bCanAccessQuicksight;
          draft.bCanAccessReviewConsolidation = action.details.bCanAccessReviewConsolidation;
          draft.clientLogoLink = action.details.clientLogoLink;
        } else {
          draft.projectId = null;
          draft.projectName = "";
          draft.organisationalStructuresID = null;
          draft.auroraDatabaseName = "";
          draft.clientUserName = "";
          draft.clientID = null;
          draft.multipleLocations = null;
          draft.userLocationID = null;
          draft.bUsesReviewPush = null;
          draft.bUsesBrightLocal = null;
          draft.bCanAccessAccountManagement = null;
          draft.bCanAccessUserManagement = null;
          draft.bCanAccessDashboard = null;
          draft.bCanAccessCommentAnalysis = null;
          draft.bCanAccessReviewFeed = null;
          draft.bCanAccessLeagueTables = null;
          draft.bCanAccessLoginReport = null;
          draft.bCanAccessResources = null;
          draft.bCanAccessGuides = null;
          draft.bCanAccessQuicksight = null;
          draft.bCanAccessReviewConsolidation = null;
        }
        break;
    }
  });
}

/* ACTION CREATORS */
export const setProjectIdValid = (
  payload: "idle" | "valid" | "invalid",
  details?: ProjectDetails
) => ({
  type: PROJECT_ID_VALID,
  payload,
  details,
});

// Simple action creator for setting project
export const setProject = (project: ProjectDetails) => ({
  type: SET_PROJECT,
  payload: project
});

/* EPICS */
const projectSetEpic = (action$: ActionsObservable<any>): Observable<any> =>
  action$.pipe(
    ofType(SET_PROJECT),
    switchMap((action) => {
      const project = action.payload;
      // Dynamically import the config and api modules to avoid circular dependencies
      return from(Promise.all([
        import('../config'),
        import('../api')
      ])).pipe(
        switchMap(([configModule, apiModule]) => {
          const { endpoints } = configModule;
          const api = apiModule.default;
          
          debugger; // Debugger statement to check if the epic is running
          
          return from(api.post(endpoints.dashboard.applyDateFilter, {
            projectId: project.id,
            databaseName: project.auroraDatabaseName,
            filters: "",  // Empty filters or default value
            bClearFilters: true
          })).pipe(
            // This just ignores the API response since we don't need to dispatch any actions with it
            ignoreElements(),
            catchError((err: AxiosError) => {
              console.error("Error calling applyDateFilter:", err);
              return [];
            })
          );
        })
      );
    })
  );

export const clientEpic = combineEpics(projectSetEpic);

/* SELECTORS */
export const selectProjectId = (app: Store) => app.client.projectId;
export const selectProjectName = (app: Store) => app.client.projectName;
export const selectOrgStructure = (app: Store) =>
  app.client.organisationalStructuresID;
export const selectDatabaseName = (app: Store) => app.client.auroraDatabaseName;
export const selectUserName = (app: Store) => app.client.clientUserName;
export const selectClientId = (app: Store) => app.client.clientID;
export const selectClientLogoLink = (app: Store) => app.client.clientLogoLink;
export const selectUserHasMultipleLoc = (app: Store) =>
  app.client.multipleLocations;
export const selectUserLocationId = (app: Store) => app.client.userLocationID;
export const selectProjectIdValid = (app: Store) => app.client.projectIdValid;
export const selectUsesReviewPush = (app: Store) => app.client.bUsesReviewPush;
export const selectUsesBrightLocal = (app: Store) => app.client.bUsesBrightLocal;
export const selectUserAccess = (app: Store) => {
  const userAccess = {
    bCanAccessAccountManagement: app.client.bCanAccessAccountManagement,
    bCanAccessUserManagement: app.client.bCanAccessUserManagement,
    bCanAccessDashboard: app.client.bCanAccessDashboard,
    bCanAccessCommentAnalysis: app.client.bCanAccessCommentAnalysis,
    bCanAccessReviewFeed: app.client.bCanAccessReviewFeed,
    bCanAccessLeagueTables: app.client.bCanAccessLeagueTables,
    bCanAccessLoginReport: app.client.bCanAccessLoginReport,
    bCanAccessResources: app.client.bCanAccessResources,
    bCanAccessGuides: app.client.bCanAccessGuides,
    bCanAccessQuicksight: app.client.bCanAccessQuicksight,
    bCanAccessReviewConsolidation: app.client.bCanAccessReviewConsolidation
  };
  return userAccess;
};